import { Link } from 'react-router-dom';
import { CalendarPlus, MousePointerClick, Eye, Wallet as WalletIcon } from 'lucide-react';
import { useMe, useReportsSummary, useBookings } from '@/api/ads/hooks';
import { PageHeader } from '@/components/PageHeader';
import { Stat } from '@/components/ui/stat';
import { Card, CardHeader, CardBody } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, Th, Td, EmptyRow } from '@/components/ui/table';
import { Spinner } from '@/components/ui/spinner';
import { npr, num, pct, fmtDate } from '@/lib/format';

export function DashboardPage() {
  const me = useMe();
  const summary = useReportsSummary();
  const bookings = useBookings();

  const recent = (bookings.data?.results ?? []).slice(0, 6);

  return (
    <>
      <PageHeader
        title={`Welcome${me.data?.advertiser.name ? `, ${me.data.advertiser.name}` : ''}`}
        description="Your advertising performance at a glance."
        action={
          <Link to="/book">
            <Button>
              <CalendarPlus className="h-4 w-4" /> Book an ad
            </Button>
          </Link>
        }
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat
          label="Wallet balance"
          value={me.isLoading ? '—' : npr(me.data?.wallet.balance)}
          hint={`Available ${npr(me.data?.wallet.available)}`}
          icon={<WalletIcon className="h-4 w-4" />}
        />
        <Stat label="Total spend" value={summary.isLoading ? '—' : npr(summary.data?.total_spend)} />
        <Stat
          label="Impressions"
          value={summary.isLoading ? '—' : num(summary.data?.impressions)}
          icon={<Eye className="h-4 w-4" />}
        />
        <Stat
          label="Clicks / CTR"
          value={summary.isLoading ? '—' : num(summary.data?.clicks)}
          hint={summary.data ? `CTR ${pct(summary.data.ctr_pct)}` : undefined}
          icon={<MousePointerClick className="h-4 w-4" />}
        />
      </div>

      <Card className="mt-5">
        <CardHeader
          title="Recent bookings"
          action={
            <Link to="/campaigns">
              <Button variant="outline" size="sm">
                View campaigns
              </Button>
            </Link>
          }
        />
        <CardBody className="p-0">
          {bookings.isLoading ? (
            <div className="p-6">
              <Spinner label="Loading bookings…" />
            </div>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Product</Th>
                  <Th>Daypart</Th>
                  <Th>Dates</Th>
                  <Th>Amount</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {recent.length === 0 && <EmptyRow colSpan={5}>No bookings yet. Book your first ad.</EmptyRow>}
                {recent.map((b) => (
                  <tr key={b.id}>
                    <Td className="font-medium">{b.product_name}</Td>
                    <Td className="text-muted-foreground">{b.daypart_label ?? 'All day'}</Td>
                    <Td className="text-muted-foreground">
                      {fmtDate(b.start_date)} → {fmtDate(b.end_date)}
                    </Td>
                    <Td>{npr(b.total_amount)}</Td>
                    <Td>
                      <Badge status={b.status}>{b.status_display}</Badge>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>
    </>
  );
}
